import React, { useEffect, useState, useCallback } from "react";
import { PRODUCT_UNITS } from "../lib/productUnits";
import { api, API, SCREEN_LABELS } from "../lib/api";
import { PageHeader, Card, Btn, Modal, Field, Input, Select, Badge, EmptyState } from "../components/kr";
import { useConfirm } from "../components/ConfirmDialog";
import { useAuth } from "../lib/auth";
import { useMultiSelect } from "../lib/multiSelect";
import MultiSelectBar from "../components/MultiSelectBar";
import Barcode from "../components/Barcode";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, TrendUp, TrendDown, FileXls, FilePdf, Barcode as BcIcon } from "@phosphor-icons/react";

export default function Products() {
  const [items, setItems] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [open, setOpen] = useState(null);
  const [tab, setTab] = useState("list");     // list | sku
  const { confirm } = useConfirm();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const ms = useMultiSelect();

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([api.get("/products"), api.get("/raw-materials")]);
    setItems(a.data); setMaterials(b.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Group SKUs by their `name` — so multiple variants of "Boondi" (e.g. ₹10,
  // ₹40, ₹80 packs) appear together as one product with a list of SKU rows.
  const grouped = React.useMemo(() => {
    const map = new Map();
    for (const s of items) {
      const key = (s.name || "Unnamed").trim();
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    }
    return Array.from(map.entries())
      .map(([name, skus]) => ({
        name,
        skus: skus.sort((a, b) => (a.pack_size || 0) - (b.pack_size || 0)
                                || (a.sale_price || 0) - (b.sale_price || 0)),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);

  const del = async (s) => {
    const ok = await confirm({
      title: "Delete product?",
      message: `${s.name}${s.sku_code ? ` (${s.sku_code})` : ""} will be removed. Existing invoices referencing it are kept.`,
      tone: "danger",
    });
    if (!ok) return;
    await api.delete(`/products/${s.id}`);
    load();
  };

  const exportExcel = async () => {
    try {
      const res = await fetch(`${API}/products/export.xlsx`, { credentials: "include" });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `skus-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      toast.success("Excel exported");
    } catch (e) {
      toast.error(e.message || "Export failed");
    }
  };

  const downloadCostSheet = async (s) => {
    try {
      const res = await fetch(`${API}/skus/${s.id}/cost-sheet.pdf`, { credentials: "include" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Cost sheet failed");
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const fname = (s.sku_code || s.name || "sku").replace(/\s+/g, "_");
      a.download = `cost-sheet-sku-${fname}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      toast.success("Cost sheet downloaded");
    } catch (e) {
      toast.error(e.message || "Cost sheet failed");
    }
  };

  return (
    <div className="p-6 lg:p-10">
      <PageHeader
        label="CATALOG"
        title="Products (SKUs)"
        subtitle="Finished-goods SKUs with GS1 retail barcodes. COGS auto-computed from live raw-material prices."
        action={
          <div className="flex gap-2">
            <Btn variant="secondary" onClick={exportExcel} data-testid="products-export"><FileXls size={14} /> Excel</Btn>
            <Btn onClick={() => setOpen("new")} data-testid="add-product"><Plus size={14} /> New SKU</Btn>
          </div>
        }
      />

      <div className="mb-4 flex gap-1 border-b border-zinc-200">
        <button
          type="button"
          onClick={() => setTab("list")}
          className={`px-4 py-2 text-xs font-mono uppercase tracking-widest ${tab === "list" ? "border-b-2 border-orange-600 text-orange-700" : "text-zinc-500"}`}
          data-testid="products-tab-list"
        >
          Full List
        </button>
        <button
          type="button"
          onClick={() => setTab("sku")}
          className={`px-4 py-2 text-xs font-mono uppercase tracking-widest ${tab === "sku" ? "border-b-2 border-orange-600 text-orange-700" : "text-zinc-500"}`}
          data-testid="products-tab-sku"
        >
          SKU Barcodes
        </button>
      </div>

      {tab === "sku" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="sku-grid">
          {items.map(s => (
            <Card key={s.id} className="!p-4" data-testid={`sku-card-${s.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="font-bold">{s.name}</div>
                  <div className="text-[10px] font-mono text-zinc-500">
                    {s.sku_code || "—"}
                    {s.pack_size ? ` · ${s.pack_size}${s.pack_unit || ""}` : ""}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => downloadCostSheet(s)} className="text-orange-600 hover:text-orange-800"
                          title="Download SKU Cost Sheet PDF" data-testid={`sku-costsheet-${s.id}`}>
                    <FilePdf size={14} weight="bold" />
                  </button>
                  <button onClick={() => setOpen(s)} className="text-orange-600 hover:text-orange-800" title="Edit">
                    <PencilSimple size={14} weight="bold" />
                  </button>
                </div>
              </div>
              {s.gs1_barcode ? (
                <div className="border border-zinc-200 p-3 bg-white">
                  <Barcode value={s.gs1_barcode} height={50} fontSize={12} />
                </div>
              ) : (
                <div className="border border-dashed border-zinc-300 p-4 text-center text-xs text-zinc-500 bg-zinc-50">
                  No GS1 barcode set.
                  <button onClick={() => setOpen(s)} className="ml-1 text-orange-600 hover:text-orange-800 underline">Add one</button>
                </div>
              )}
              <div className="mt-2 flex justify-between text-xs font-mono">
                <span>Sale: <b>₹{s.sale_price}</b></span>
                <span className={s.margin > 0 ? "text-green-700" : "text-red-700"}>Margin ₹{s.margin}</span>
              </div>
            </Card>
          ))}
          {items.length === 0 && (
            <div className="col-span-full"><EmptyState title="No SKUs yet" icon={BcIcon} /></div>
          )}
        </div>
      ) : (
        <Card className="!p-0">
        {items.length === 0 ? (
          <div className="p-6"><EmptyState title="No products yet" hint="Add your first SKU (e.g. 'Potato Chips 100g') and define its recipe." /></div>
        ) : (
          <table className="kr-table">
            <thead><tr>
              {isAdmin && (
                <th className="w-8">
                  <input type="checkbox" checked={ms.selected.length > 0 && ms.selected.length === items.length}
                    onChange={() => ms.toggleAll(items.map(x => x.id))} className="accent-orange-600" />
                </th>
              )}
              <th>SKU / Variant</th><th>Latest COGS</th><th>Sale Price</th><th>Margin</th><th></th>
            </tr></thead>
            <tbody>
              {grouped.map(g => (
                <React.Fragment key={g.name}>
                  {g.skus.length > 1 && (
                    <tr className="bg-orange-50 border-t-2 border-orange-200" data-testid={`product-group-${g.name}`}>
                      <td colSpan={isAdmin ? 6 : 5} className="!py-2">
                        <div className="flex items-center gap-2">
                          <span className="font-black text-sm tracking-tight">{g.name}</span>
                          <Badge tone="orange">{g.skus.length} variants</Badge>
                          <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">
                            batch prefixes: {[...new Set(g.skus.map(s => (s.batch_prefix || "").trim() || "—"))].join(" · ")}
                          </span>
                        </div>
                      </td>
                    </tr>
                  )}
                  {g.skus.map(s => (
                    <tr key={s.id} data-testid={`product-${s.id}`}>
                      {isAdmin && (
                        <td>
                          <input type="checkbox" checked={ms.isSelected(s.id)} onChange={() => ms.toggle(s.id)} className="accent-orange-600" data-testid={`product-check-${s.id}`} />
                        </td>
                      )}
                      <td>
                        {g.skus.length > 1 ? (
                          <div className="pl-3 border-l-2 border-orange-300">
                            <div className="font-semibold text-sm">
                              {s.pack_size ? `${s.pack_size}${s.pack_unit || ""} · ` : ""}
                              <span className="font-mono">₹{s.sale_price}</span>
                            </div>
                            <div className="text-[10px] font-mono text-zinc-500">
                              {s.sku_code || "—"}
                              {s.batch_prefix ? <> · <span className="text-orange-700">Batch: {s.batch_prefix}-#####</span></> : null}
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div className="font-bold">{s.name}</div>
                            <div className="text-[10px] font-mono text-zinc-500">
                              {s.sku_code || "—"}
                              {s.batch_prefix ? <> · <span className="text-orange-700">Batch: {s.batch_prefix}-#####</span></> : null}
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="font-mono">
                        {s.cogs !== null && s.cogs !== undefined ? (
                          <div>
                            <div className="font-bold">₹{s.cogs}</div>
                            <div className="text-[9px] text-zinc-500">
                              {s.cogs_source_batch ? `from ${s.cogs_source_batch}` : ""}
                            </div>
                          </div>
                        ) : <span className="text-zinc-300">—</span>}
                      </td>
                      <td className="font-mono font-bold">₹{s.sale_price}</td>
                      <td className="font-mono">
                        {s.margin !== null && s.margin !== undefined ? (
                          <div className={`font-bold ${s.margin > 0 ? "text-green-700" : "text-red-700"}`}>
                            {s.margin > 0 ? <TrendUp size={12} weight="bold" className="inline" /> : <TrendDown size={12} weight="bold" className="inline" />}
                            ₹{s.margin} <span className="text-xs">({s.margin_pct}%)</span>
                          </div>
                        ) : <span className="text-zinc-300">—</span>}
                      </td>
                      <td className="flex gap-2">
                        <button onClick={() => downloadCostSheet(s)} className="text-orange-600" title="Cost sheet PDF"
                                data-testid={`sku-costsheet-row-${s.id}`}>
                          <FilePdf size={14} weight="bold" />
                        </button>
                        <button onClick={() => setOpen(s)} className="text-orange-600"><PencilSimple size={14} weight="bold" /></button>
                        <button onClick={() => del(s)} className="text-red-600"><Trash size={14} weight="bold" /></button>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      )}

      {open && <ProductModal sku={open === "new" ? null : open} materials={materials}
        onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} />}
      <MultiSelectBar
        count={ms.selected.length}
        ids={ms.selected}
        resource="products"
        label="products"
        onCleared={() => { ms.clear(); load(); }}
      />
    </div>
  );
}

function ProductModal({ sku, materials, onClose, onSaved }) {
  const isNew = !sku;
  const [f, setF] = useState(sku ? { ...sku } : {
    name: "", sku_code: "", sale_price: "", gst_rate: 18, unit: "packets", recipe: [], description: "",
    hsn: "",
    gs1_barcode: "", pack_size: "", pack_unit: "g", active: true, batch_prefix: "",
    sale_price_inclusive: false,
  });
  const upd = (k, v) => setF({ ...f, [k]: v });

  // When the operator flags sale_price as GST-inclusive, keep the DISPLAYED
  // figure stable across toggle + gst-rate edits by mutating the underlying
  // pre-tax `sale_price` in the opposite direction.
  const salePriceDisplay = (() => {
    const p = parseFloat(f.sale_price) || 0;
    const g = parseFloat(f.gst_rate) || 0;
    return f.sale_price_inclusive && p > 0 ? +(p * (1 + g / 100)).toFixed(4) : f.sale_price;
  })();
  const onSalePriceChange = (raw) => {
    const typed = parseFloat(raw) || 0;
    const g = parseFloat(f.gst_rate) || 0;
    const preTax = f.sale_price_inclusive && g > 0
      ? +(typed / (1 + g / 100)).toFixed(4)
      : typed;
    setF({ ...f, sale_price: preTax });
  };
  const toggleInclusive = (nextInc) => {
    const g = parseFloat(f.gst_rate) || 0;
    const currentDisplay = f.sale_price_inclusive && g > 0
      ? +((parseFloat(f.sale_price) || 0) * (1 + g / 100)).toFixed(4)
      : (parseFloat(f.sale_price) || 0);
    const nextPrice = nextInc && g > 0
      ? +(currentDisplay / (1 + g / 100)).toFixed(4)
      : currentDisplay;
    setF({ ...f, sale_price_inclusive: nextInc, sale_price: nextPrice });
  };
  const onGstChange = (raw) => {
    const nextGst = raw === "" ? "" : parseFloat(raw) || 0;
    if (f.sale_price_inclusive) {
      const oldG = parseFloat(f.gst_rate) || 0;
      const oldP = parseFloat(f.sale_price) || 0;
      const display = +(oldP * (1 + oldG / 100)).toFixed(4);
      const newG = parseFloat(nextGst) || 0;
      const newPrice = newG > 0 ? +(display / (1 + newG / 100)).toFixed(4) : display;
      setF({ ...f, gst_rate: nextGst, sale_price: newPrice });
    } else {
      setF({ ...f, gst_rate: nextGst });
    }
  };
  const updRecipe = (i, k, v) => {
    const r = [...f.recipe]; r[i] = { ...r[i], [k]: v }; setF({ ...f, recipe: r });
  };
  const addRow = () => setF({ ...f, recipe: [...f.recipe, { raw_material_id: materials[0]?.id || "", qty: 1, unit: "kg" }] });
  const rmRow = (i) => setF({ ...f, recipe: f.recipe.filter((_, x) => x !== i) });

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { ...f, sale_price: parseFloat(f.sale_price), gst_rate: parseFloat(f.gst_rate),
        sale_price_inclusive: !!f.sale_price_inclusive,
        pack_size: parseFloat(f.pack_size) || 0,
        recipe: f.recipe.map(r => ({ raw_material_id: r.raw_material_id, qty: parseFloat(r.qty), unit: r.unit })) };
      if (isNew) await api.post("/products", body);
      else await api.put(`/products/${sku.id}`, body);
      toast.success(isNew ? "Added" : "Saved");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Modal open onClose={onClose} title={isNew ? "New SKU" : `Edit · ${sku.name}`} size="lg">
      <form onSubmit={submit} className="space-y-4" data-testid="product-form">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Product Name" full><Input value={f.name} onChange={e => upd("name", e.target.value)} required data-testid="prod-name" /></Field>
          <Field label="SKU Code"><Input value={f.sku_code} onChange={e => upd("sku_code", e.target.value)} data-testid="prod-code" /></Field>
          <Field label={f.sale_price_inclusive ? "Sale Price (₹, incl. GST)" : "Sale Price (₹, pre-tax)"}>
            <Input
              type="number"
              step="0.01"
              value={salePriceDisplay}
              onChange={e => onSalePriceChange(e.target.value)}
              required
              className={f.sale_price_inclusive ? "border-emerald-300 bg-emerald-50/40" : ""}
              data-testid="prod-price"
            />
            <label className="mt-1 flex items-center gap-1.5 text-[10px] font-mono text-zinc-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={!!f.sale_price_inclusive}
                onChange={e => toggleInclusive(e.target.checked)}
                className="w-3 h-3 accent-emerald-600"
                data-testid="prod-price-inclusive"
              />
              Sale price is inclusive of GST
            </label>
          </Field>
          <Field label="Unit">
            <Select value={f.unit} onChange={e => upd("unit", e.target.value)} data-testid="prod-unit">
              {PRODUCT_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
              {/* Preserve any legacy unit already on the SKU so editing an
                  older row doesn't silently overwrite it. */}
              {f.unit && !PRODUCT_UNITS.includes(f.unit) && (
                <option value={f.unit}>{f.unit} (legacy)</option>
              )}
            </Select>
          </Field>
          <Field label="GST %"><Input type="number" step="0.01" value={f.gst_rate} onChange={e => onGstChange(e.target.value)} /></Field>
        </div>

        <Field label="HSN / SAC code (auto-flows into every invoice line for this SKU)" full>
          <Input
            value={f.hsn || ""}
            onChange={e => upd("hsn", e.target.value.trim())}
            placeholder="e.g. 21069099"
            maxLength={12}
            data-testid="prod-hsn"
          />
        </Field>

        <Field label="Description (optional — appears on invoices, quotations and product cards)" full>
          <textarea
            value={f.description || ""}
            onChange={e => upd("description", e.target.value)}
            rows={2}
            placeholder="Short blurb — e.g. Handcrafted potato chips, salted, 100g pouch"
            className="w-full border border-zinc-300 px-2 py-1.5 text-sm rounded focus:outline-none focus:ring-1 focus:ring-orange-500"
            data-testid="prod-description"
          />
        </Field>

        <div className="border-t border-zinc-200 pt-3">
          <div className="kr-label mb-2">Retail Packaging (GS1 India Barcode)</div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="GS1 Barcode (13-digit EAN)" full>
              <Input
                value={f.gs1_barcode || ""}
                onChange={e => upd("gs1_barcode", e.target.value.replace(/[^0-9]/g, "").slice(0, 14))}
                placeholder="8901234567890"
                className="font-mono"
                data-testid="prod-gs1"
              />
            </Field>
            <Field label="Pack Size">
              <Input type="number" step="0.01" value={f.pack_size || ""} onChange={e => upd("pack_size", e.target.value)} placeholder="100" data-testid="prod-pack-size" />
            </Field>
            <Field label="Pack Unit">
              <Select value={f.pack_unit || "g"} onChange={e => upd("pack_unit", e.target.value)} data-testid="prod-pack-unit">
                <option value="g">g</option>
                <option value="kg">kg</option>
                <option value="ml">ml</option>
                <option value="l">l</option>
                <option value="pcs">pcs</option>
              </Select>
            </Field>
          </div>
          <div className="text-[10px] text-zinc-500 mt-1">
            Generate the EAN-13 barcode at <a href="https://gs1india.org" target="_blank" rel="noreferrer" className="text-orange-600 underline">gs1india.org</a>, then paste the number here — the app renders it as a scannable barcode.
          </div>
        </div>

        <div className="border-t border-zinc-200 pt-3">
          <div className="kr-label mb-2">Batch numbering</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Batch Prefix (per-SKU)" full>
              <Input
                value={f.batch_prefix || ""}
                onChange={e => upd("batch_prefix", e.target.value.replace(/[^A-Za-z0-9-]/g, "").toUpperCase().slice(0, 12))}
                placeholder="e.g. BOO10 for Boondi ₹10 · BOO40 for ₹40"
                className="font-mono uppercase"
                data-testid="prod-batch-prefix"
              />
            </Field>
          </div>
          <div className="text-[10px] text-zinc-500 mt-1">
            Each SKU variant of the same product can use its own batch prefix — so batches produced for the ₹10 pack are numbered <span className="font-mono">BOO10-0001…</span> while the ₹40 pack becomes <span className="font-mono">BOO40-0001…</span>.
            Leave blank to fall back to the global batch prefix defined in <span className="font-mono">Settings → Documents</span>.
          </div>
        </div>
        <div className="bg-zinc-50 border-l-4 border-zinc-400 p-3 text-xs text-zinc-700 leading-relaxed">
          <div className="font-bold uppercase tracking-widest text-[10px] text-zinc-500 mb-1">Recipe deprecated</div>
          COGS is now derived from your actual production batches. Create a batch in <span className="font-mono">Batches</span> and add this SKU as an output — the per-pack cost will appear on this page automatically.
        </div>
        <div className="flex justify-end gap-2">
          <Btn type="button" variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" data-testid="prod-submit">{isNew ? "Create" : "Save"}</Btn>
        </div>
      </form>
    </Modal>
  );
}
